import React, { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ModalBottomButtonContainer } from './Styles';
import { DangerButton, SuccessButton } from '../connectedForm/Button';
import ModalContext from '../Context/ModalContext';
import QueryDropdown from '../connectedForm/QueryDropdown';
import * as entities from '../../graphql/entities';
import { BlurTextField } from '../connectedForm/inputComponents';
import InitializeMotoPayment from 'graphql/mutations/sales/InitializeMotoPayment.graphql';
import MotoPaymentStatus from 'graphql/queries/sales/MotoPaymentStatus.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { serviceActions } from '../../state/modules/service';
import CurrencyContext from '../Context/CurrencyContext';
import { default as Form } from '../connectedForm';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { BasicHeader } from '../../actions/BasicHeader';
import {
  AlertType,
  FilterOperation,
  IAddress,
  IBank,
  ICountry,
  IPaymentMethod,
  IPaymentMethodBank,
  Maybe,
  PaymentType,
} from '../../graphql/schema/p4types';
import { BlurTextFieldType, HrefLink } from '../connectedForm/BlurTextField/BlurTextField';
import Fieldset from '../connectedForm/Fieldset';
import loadConnectClient from '../../utils/loadConnectClient';
import AppContext from '../Context/AppContext';
import DateField from '../../components/connectedForm/DateInput/DateInput';
import { format } from 'date-fns';
import Row from '../connectedForm/Row';
import FluentModal from './FluentModal';

const ConnectIframeContainer = styled.div`
  max-height: 100%;

  > iframe {
    border: none;
    width: 100%;
    height: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
`;

// styles for connect iframe inputs
const connectStyles = {
  base: {
    default: {
      color: 'black',
      textDecoration: 'none',
      fontFamily: "'Do Hyeon', sans-serif",
      boxSizing: 'border-box',
      padding: '.375rem .75rem',
      boxShadow: 'none',
      fontSize: '1rem',
      borderRadius: '.25rem',
      lineHeight: '1.5',
      backgroundColor: '#fff',
    },
    focus: {
      color: '#495057',
      borderColor: '#80bdff',
    },
    error: {
      color: '#B00',
      borderColor: '#B00',
    },
    valid: {
      color: 'green',
      borderColor: 'green',
    },
    label: {
      display: 'none',
    },
  },
  cv2: {
    container: {
      width: '25%',
      float: 'left',
      boxSizing: 'border-box',
    },
    default: {
      borderRadius: '0 .25rem .25rem 0',
    },
  },
  expiryDate: {
    container: {
      width: '25%',
      float: 'left',
      borderRadius: '0rem',
    },
    default: {
      borderRadius: '0',
      borderRight: 'none',
    },
  },
  cardNumber: {
    container: {
      width: '50%',
      float: 'left',
    },
    default: {
      borderRadius: '.25rem 0 0 .25rem',
      borderRight: 'none',
    },
  },
};

// hack to get a promise back off useQuery
const useImperativeQuery = (query, options) => {
  const { refetch } = useQuery(query, { ...options, skip: true });

  return (variables) => {
    return refetch(variables);
  };
};

type IBillingAddress = {
  address1: Maybe<string>;
  address2: Maybe<string>;
  address3: Maybe<string>;
  address4: Maybe<string>;
  city: Maybe<string>;
  state: Maybe<string>;
  postcode: Maybe<string>;
  countryCode: Maybe<ICountry>;
};

type ISalesOrderPaymentModalProps = {
  isOpen: boolean;
  orderId: number | undefined;
  billingAddress: Maybe<IAddress>;
  emailAddress: string;
  warehouseId: number | undefined;
  paymentMethodDefault: Maybe<IPaymentMethod>;
};

const SalesOrderPaymentModal = ({
  isOpen,
  orderId,
  billingAddress: customerAddress,
  emailAddress: email,
  warehouseId,
  paymentMethodDefault,
}: ISalesOrderPaymentModalProps) => {
  const connectedForm = useContext(ConnectedFormContext);

  // state
  const [paymentMethod, setPaymentMethod] = useState<Maybe<IPaymentMethod>>(null);
  const [bankAccount, setBankAccount] = useState<Maybe<IBank>>(null);
  const [reference, setReference] = useState<string>('');
  const [paymentValue, setPaymentValue] = useState<Maybe<string | number>>(null);
  const [motoAccessToken, setMotoAccessToken] = useState(null);
  const [connectE, setConnectE] = useState<any>(null);
  const [connectPaymentLoading, setConnectPaymentLoading] = useState(false);
  const [receiptDate, setReceiptDate] = useState<string | null>(format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    setPaymentMethod(paymentMethodDefault);
  }, [paymentMethodDefault]);

  const [emailAddress, setEmailAddress] = useState<string>(email);
  const [billingAddress, setBillingAddress] = useState<IBillingAddress>({
    address1: customerAddress?.line1,
    address2: customerAddress?.line2,
    address3: customerAddress?.line3,
    address4: null,
    city: customerAddress?.line4,
    state: customerAddress?.line5,
    postcode: customerAddress?.postCode,
    countryCode: customerAddress?.country,
  });

  // context
  const { onClose: onModalClose } = useContext(ModalContext);
  const { currency } = useContext(CurrencyContext);
  const app = useContext(AppContext);
  const dispatch = useDispatch();

  // queries
  const handleQueryError = useComponentQueryErrorHandling();
  const [fetchAccessToken, { loading: fetchAccessTokenLoading }] = useMutation(InitializeMotoPayment, {
    onError: handleQueryError,
  });

  const getPaymentStatus = useImperativeQuery(MotoPaymentStatus, {
    onError: handleQueryError,
  });

  useEffect(() => {
    if (!motoAccessToken) {
      return;
    }

    const paymentDetails = {
      amount: (parseFloat(paymentValue as string) * 100).toFixed(0), // covert to min unit
      currencyCode: `${currency?.isoCode?.codeNumeric || 826}`, // 826 = GBP
      paymentToken: motoAccessToken,
    };

    const config = {
      containerId: 'connectPayment',
      paymentDetails,
      styles: connectStyles,
    };

    // if we've made one before just update the access token
    if (!connectE) {
      // grab off window, included in index.html
      loadConnectClient(() => {
        setConnectE(new (window as any).Connect.ConnectE(config));
      }, !app?.fullUserDetails?.flags?.motoTestMode);
    } else {
      connectE.updateAccessToken(paymentDetails);
    }
  }, [motoAccessToken]);

  useEffect(() => {
    setBillingAddress({
      address1: customerAddress?.line1,
      address2: customerAddress?.line2,
      address3: customerAddress?.line3,
      address4: null,
      city: customerAddress?.line4,
      state: customerAddress?.line5,
      postcode: customerAddress?.postCode,
      countryCode: customerAddress?.country,
    });
  }, [customerAddress]);

  useEffect(() => {
    setEmailAddress(email);
  }, [email]);

  /**
   * Fetches a new MOTO access token
   */
  const updateMotoAccessToken = () => {
    // fetch access token
    fetchAccessToken({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          orderId,
          paymentValue,
        },
      },
    }).then((response) => {
      setMotoAccessToken(response?.data?.initializeMotoPayment?.result);
    });
  };

  const onClose = () => {
    // clean up
    setMotoAccessToken(null);
    setConnectE(null);
    setPaymentMethod(null);
    setPaymentValue(null);
    onModalClose?.();
  };

  const onConfirm = () => {
    if (paymentMethod?.paymentType === 'MOTO') {
      updateMotoAccessToken();
    } else {
      handlePaymentComplete();
    }
  };

  const handlePaymentComplete = (additionalInfo?: string) => {
    let paymentReference: string = reference;

    if (additionalInfo) {
      paymentReference = (!paymentReference ? '' : ' ') + additionalInfo;
    }

    onClose();

    dispatch(
      connectedForm.change('newPaymentLines', [
        {
          paymentMethod,
          bankAccount,
          reference: paymentReference,
          paymentValue,
          receiptDate,
        },
      ])
    );
  };

  const handlePaymentFailure = (message) => {
    dispatch(serviceActions.genericAlert('Payment Request Failed', message, AlertType.Warning));
    updateMotoAccessToken();
  };

  const onPay = () => {
    setConnectPaymentLoading(true);

    const additionalInfo = {
      userEmailAddress: emailAddress,
      billingAddress: {
        ...billingAddress,
        countryCode: billingAddress?.countryCode?.isoCountry?.codeNumeric,
      },
    };

    connectE
      .executePayment(additionalInfo)
      .then((data) => {
        // confirm status code via API
        getPaymentStatus({
          accessToken: motoAccessToken,
        }).then((response) => {
          // 0 = success
          if (response.data.motoPaymentStatus !== 0) {
            handlePaymentFailure(data.message);
          } else {
            handlePaymentComplete(`Auth code: ${data.authCode}`);
          }
        });
      })
      .catch((errors: { message: string } | { message: string }[]) => {
        if (errors) {
          let arrayErrors = errors;
          if (!Array.isArray(errors)) {
            arrayErrors = [errors];
          }

          for (const { message } of arrayErrors) {
            console.error(message);
            handlePaymentFailure(message);
          }
        } else {
          handlePaymentFailure('An unknown error has occurred, please try again');
        }
      })
      .finally(() => {
        setConnectPaymentLoading(false);
      });
  };

  const renderForm = () => {
    const valid = paymentMethod && bankAccount && paymentValue && parseFloat(paymentValue as string) > 0.0;

    return (
      <>
        <QueryDropdown
          {...entities.PaymentMethod.dropdown}
          label="Payment Method"
          onChange={(value: IPaymentMethod) => {
            setPaymentMethod(value);
            let bank = value.bankMappings?.find((mapping: Maybe<IPaymentMethodBank>) => {
              return mapping?.warehouse?.id === warehouseId;
            })?.bank;

            if (!bank) {
              bank = value.bankMappings?.find(
                (mapping: Maybe<IPaymentMethodBank>) =>
                  mapping?.warehouse === null && mapping.tradeCounterTerminal === null
              )?.bank;
            }

            if (bank) {
              setBankAccount(bank);
            }
          }}
          value={paymentMethod}
          predefinedFilters={[
            {
              filterOperation: FilterOperation.NotEquals,
              filterParameters: {
                field: 'paymentType',
                value: PaymentType.Pac,
              },
            },
            {
              filterOperation: FilterOperation.NotEquals,
              filterParameters: {
                field: 'paymentType',
                value: PaymentType.PayByLink,
              },
            },
            ...entities.PaymentMethod.dropdown?.predefinedFilters,
          ]}
          mandatory={true}
          data-testid="so-payment-modal-method"
        />
        <QueryDropdown
          {...entities.Bank.dropdown}
          label="Bank Account"
          onChange={setBankAccount}
          value={bankAccount}
          mandatory={true}
          data-testid="so-payment-modal-bank-account"
        />
        <BlurTextField
          label="Reference"
          value={reference}
          onChange={setReference}
          enableOnChange
          data-testid="so-payment-modal-reference"
        />
        <BlurTextField
          label="Payment Amount"
          value={paymentValue as string | number}
          onChange={setPaymentValue}
          mandatory={true}
          type={BlurTextFieldType.Currency}
          decimals={2}
          enableOnChange
          data-testid="so-payment-modal-amount"
        />
        <DateField
          label="Receipt Date"
          value={receiptDate}
          onChange={setReceiptDate}
          data-testid="sales-order-payment-date"
        />
        <ModalBottomButtonContainer>
          <SuccessButton
            inline
            onClick={onConfirm}
            disabled={!valid || fetchAccessTokenLoading}
            data-testid="so-payment-modal-confirm"
          >
            Confirm
          </SuccessButton>
          <DangerButton
            inline
            onClick={onClose}
            disabled={fetchAccessTokenLoading}
            data-testid="so-payment-modal-cancel"
          >
            Cancel
          </DangerButton>
        </ModalBottomButtonContainer>
      </>
    );
  };

  const renderMotoPayment = () => {
    return (
      <>
        <BlurTextField
          label="Payment Amount"
          value={paymentValue as string}
          type={BlurTextFieldType.Currency}
          decimals={2}
          readOnly
        />
        <ConnectIframeContainer id="connectPayment" />
        <Form.Legend label="Billing Address" />
        <BlurTextField
          label="Email Address"
          hyperlink={HrefLink.MailTo}
          value={emailAddress}
          onChange={setEmailAddress}
          enableOnChange
          data-testid="so-payment-moto-email"
        />
        <BlurTextField
          label="Line 1"
          value={billingAddress?.address1}
          onChange={(value) => setBillingAddress({ ...billingAddress, address1: value })}
          enableOnChange
          data-testid="so-payment-moto-line-1"
        />
        <BlurTextField
          label="Line 2"
          value={billingAddress?.address2}
          onChange={(value) => setBillingAddress({ ...billingAddress, address2: value })}
          enableOnChange
          data-testid="so-payment-moto-line-2"
        />
        <BlurTextField
          label="Line 3"
          value={billingAddress?.address3}
          onChange={(value) => setBillingAddress({ ...billingAddress, address3: value })}
          enableOnChange
          data-testid="so-payment-moto-line-3"
        />
        <BlurTextField
          label="Line 4"
          value={billingAddress?.address4}
          onChange={(value) => setBillingAddress({ ...billingAddress, address4: value })}
          enableOnChange
          data-testid="so-payment-moto-line-4"
        />
        <BlurTextField
          label="City"
          value={billingAddress?.city}
          onChange={(value) => setBillingAddress({ ...billingAddress, city: value })}
          enableOnChange
          data-testid="so-payment-moto-city"
        />
        <BlurTextField
          label="State"
          value={billingAddress?.state}
          onChange={(value) => setBillingAddress({ ...billingAddress, state: value })}
          enableOnChange
          data-testid="so-payment-moto-state"
        />
        <BlurTextField
          label="Post Code"
          value={billingAddress?.postcode}
          onChange={(value) => setBillingAddress({ ...billingAddress, postcode: value })}
          enableOnChange
          data-testid="so-payment-moto-postcode"
        />
        <QueryDropdown
          {...entities.Country.dropdown}
          label="Country"
          onChange={(value) => {
            setBillingAddress({ ...billingAddress, countryCode: value });
          }}
          value={billingAddress?.countryCode}
          data-testid="so-payment-moto-country"
        />
        <ButtonContainer>
          <SuccessButton inline onClick={onPay} disabled={connectPaymentLoading} data-testid="so-payment-moto-pay">
            Pay
          </SuccessButton>
          <DangerButton inline onClick={onClose} disabled={connectPaymentLoading} data-testid="so-payment-moto-cancel">
            Cancel
          </DangerButton>
        </ButtonContainer>
      </>
    );
  };

  return (
    <FluentModal styles={{ main: { paddingBottom: '60px', minWidth: '800px' } }} isOpen={isOpen}>
      <BasicHeader>Add Payment</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Row style={{ marginLeft: 0, marginRight: 0 }}>
          <Fieldset style={{ marginLeft: 0 }} cols={1}>
            {motoAccessToken ? renderMotoPayment() : renderForm()}
          </Fieldset>
        </Row>
      </div>
    </FluentModal>
  );
};

export default SalesOrderPaymentModal;
